import { useEffect, useState } from 'react';

import moment from 'moment';

import RadarChart from 'components/RadarChart';
import { TEXT_SDQ_REPORT } from 'constants/string/texts';
import { groupSdqSurveyBehaviorsForDisplay, hasNewSdqAnalysisFormat } from 'utils/sdqAnalysis';

import AthleticAbility from './AthleticAbility';
import SdqNewReportSections from './SdqNewReportSections';

const AthleticAbilityIcon_1 = require('assets/resources/AthleticAbilityIcon_1.svg').default;
const NoResultReport = require('assets/resources/NoResultReport.svg').default;

const ResultReport = (props) => {
  const { dataReport, dataAnalysis } = props;
  const [analysis_content, setAnalysis_content] = useState(null);
  const [integrated_analysis_content, setIntegrated_analysis_content] = useState(null);

  useEffect(() => {
    setAnalysis_content(dataAnalysis?.analysis_content || null);
    setIntegrated_analysis_content(dataAnalysis?.integrated_analysis_content || null);
  }, [dataAnalysis]);

  const analysisResults = analysis_content?.analysis_results;
  /**
   * Reports carrying `analysis_summary` + `risk_levels` get the new sectioned
   * report the guardian app shows; older ones keep the single evaluation summary
   * plus the separate 気になる行動対処法 block.
   */
  const isNewSdqReport = hasNewSdqAnalysisFormat(analysisResults);
  const groupedSdqSurveyBehaviors = groupSdqSurveyBehaviorsForDisplay(analysisResults?.child_behavior_issues);

  return (
    <div className="w-full">
      {analysis_content?.child_id && (
        <>
          <div className="flex items-center space-x-6 text-black/85 text-sm mb-8">
            <span>
              {'生成日'}：{moment(dataReport?.created_at).format('YYYY/MM/DD')}
            </span>
          </div>

          <div className="mt-8">
            <h1 className="text-xl font-bold text-black-102">{TEXT_SDQ_REPORT.SDQ_RESULT}</h1>
            <p className="text-sm text-secondary-354">{TEXT_SDQ_REPORT.NOTE}</p>
            <div className="mt-2 py-6 px-8 border border-white-714/50 rounded-lg">
              <div className={`w-full ${isNewSdqReport ? 'h-64' : 'h-50'}`}>
                <RadarChart data={analysisResults?.score_summary || {}} thresholds={analysisResults?.thresholds} />
              </div>
              {isNewSdqReport ? (
                <SdqNewReportSections analysisResults={analysisResults} />
              ) : (
                <div className="mt-6 text-black-102" dangerouslySetInnerHTML={{ __html: analysisResults?.evaluation?.summary ?? '' }} />
              )}
            </div>
          </div>

          <div className="mt-8">
            <h1 className="text-xl font-bold text-black-102">{TEXT_SDQ_REPORT.ATHLETIC_ABILITY}</h1>
            <div className="mt-2 py-6 px-8 border border-white-714/50 rounded-lg">
              <AthleticAbility data={integrated_analysis_content} />
              {integrated_analysis_content?.analysis_results?.comments && (
                <>
                  <div className="flex items-center gap-1 mt-6">
                    <img src={AthleticAbilityIcon_1} alt="AthleticAbilityIcon_1" width={20} height={20} />
                    <p className="font-bold">{TEXT_SDQ_REPORT.COMMENT_PART}</p>
                  </div>
                  <div
                    className="mt-2 text-sm text-black-102"
                    dangerouslySetInnerHTML={{ __html: integrated_analysis_content?.analysis_results?.comments ?? '' }}
                  ></div>
                </>
              )}
            </div>
          </div>
          {/* The new report already lists the behaviours inside its 対処法 sections, so this block is legacy only. */}
          {!isNewSdqReport && groupedSdqSurveyBehaviors.length > 0 && (
            <div className="mt-8">
              <h1 className="text-xl font-bold text-black-102">{TEXT_SDQ_REPORT.BEHAVIOR_ISSUES}</h1>
              {groupedSdqSurveyBehaviors.map((sdq, indexSDQ) => (
                <div key={sdq.subscale || indexSDQ} className="mt-2 py-6 px-8 border border-white-714/50 rounded-lg">
                  {(sdq.label || sdq.subscale) && (
                    <div className="flex justify-between items-center gap-1">
                      <div className="mx-auto">
                        <p className="font-bold">
                          「{sdq.label ?? sdq.subscale}」{TEXT_SDQ_REPORT.BEHAVIOR_ISSUES_EXAMPLE}
                        </p>
                        <div className="w-full h-0.25 mt-2 bg-gray-489"></div>
                      </div>
                    </div>
                  )}
                  <div className="mt-4 text-sm text-black-102">
                    <ul className="list-disc pl-5">
                      {sdq.issues.map((item, index) => (
                        <li key={index} className="mt-4">
                          「{item}」
                        </li>
                      ))}
                      {indexSDQ === groupedSdqSurveyBehaviors.length - 1 &&
                        analysisResults?.child_behavior_issues?.sdq_mchat?.map((item, index) => (
                          <li key={index} className="mt-4">
                            「{item}」
                          </li>
                        ))}
                      {indexSDQ === groupedSdqSurveyBehaviors.length - 1 &&
                        analysisResults?.child_behavior_issues?.sdq_dipole?.map((item, index) => (
                          <li key={index} className="mt-4">
                            「{item}」
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {!analysis_content?.child_id && (
        <div className="no-result-report bg-white w-full h-81.5 flex justify-center items-center">
          <img src={NoResultReport} alt="NoResultReport" />
        </div>
      )}
    </div>
  );
};

export default ResultReport;
